import { GroupBase } from 'react-select';
import {
  SelectOption,
  SelectOptionGroup,
  SelectOptionsOrGroups,
} from './types';
import { isTruthy } from '@ardoq/common-helpers';
import { MinimalIconProps } from '@ardoq/icons';

const prependGroup = <TValue>(
  options: SelectOption<TValue>[],
  group: SelectOptionGroup<TValue>
) => [group, ...options];

const isGroupBasedOption = <TValue>(
  option: SelectOption<TValue> | GroupBase<SelectOption<TValue>>
): option is GroupBase<SelectOption<TValue>> => 'options' in option;

const optionOrGroupToOptions = <TValue>(
  optionOrGroup: SelectOption<TValue> | GroupBase<SelectOption<TValue>>
): readonly SelectOption<TValue>[] =>
  isGroupBasedOption(optionOrGroup) ? optionOrGroup.options : [optionOrGroup];

const isFlatListOfOptions = <TValue>(
  optionsOrGroups: SelectOptionsOrGroups<TValue>
): optionsOrGroups is SelectOption<TValue>[] =>
  !optionsOrGroups.some(isGroupBasedOption);

const optionsOrGroupsToFlatOptions = <TValue>(
  optionsOrGroups: SelectOptionsOrGroups<TValue> = []
): SelectOption<TValue>[] =>
  isFlatListOfOptions(optionsOrGroups)
    ? optionsOrGroups
    : optionsOrGroups.flatMap(optionOrGroupToOptions);

const filter = <TValue>(
  selectOptions: SelectOptionsOrGroups<TValue>,
  predicate: (option: SelectOption<TValue>) => boolean
): SelectOptionsOrGroups<TValue> =>
  selectOptions
    .map(option => {
      if (isGroupBasedOption(option)) {
        const filteredGroupOptions = option.options.filter(predicate);
        return filteredGroupOptions.length
          ? { ...option, options: filteredGroupOptions }
          : null;
      }
      return predicate(option) ? option : null;
    })
    .filter(isTruthy);

const getIconProps = <TValue>(
  { iconName, rightIconName, leftIcon, rightIcon }: SelectOption<TValue>,
  side: 'left' | 'right'
): MinimalIconProps | undefined => {
  if (side === 'left') {
    return iconName
      ? {
          name: iconName,
        }
      : leftIcon
        ? leftIcon
        : undefined;
  }
  return rightIconName
    ? {
        name: rightIconName,
      }
    : rightIcon
      ? rightIcon
      : undefined;
};

export const selectOptionOperations = {
  prependGroup,
  isGroupBasedOption,
  filter,
  getIconProps,
  optionsOrGroupsToFlatOptions,
};
